import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, RefreshCw, Undo2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import DaysMissedReport from "@/components/dashboard/DaysMissedReport";
import PayrollYearReport from "@/components/dashboard/payroll/PayrollYearReport";
import AccountingYearReport from "@/components/dashboard/accounting/AccountingYearReport";

const money = (n) =>
  `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtWeek = (weekStart) => {
  const s = new Date(`${weekStart}T12:00:00`);
  const e = new Date(s);
  e.setDate(e.getDate() + 6);
  const opt = { month: "short", day: "numeric" };
  return `${s.toLocaleDateString("en-US", opt)} – ${e.toLocaleDateString("en-US", { ...opt, year: "numeric" })}`;
};

/**
 * Reports (owner/superadmin) — being rebuilt section by section.
 * Section 1: Saved payrolls manager. Lists every payroll week saved in the
 * app; admins can unsave a single employee's stub or an entire week, which
 * removes it from every employee's past pay periods (and from vacation-bank,
 * yearly-stat and CRA totals, which all read saved paystubs).
 */
const ReportsTab = () => {
  const { user } = useAuth();
  // Managers see Days Missed but not the payroll-unsave manager (that
  // endpoint is owner/superadmin/admin only).
  const canManagePayrolls = ["owner", "superadmin", "admin"].includes(user?.role || "");
  const [weeks, setWeeks] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    if (!canManagePayrolls) return;
    try {
      const r = await api.get("/payroll/saved-weeks");
      setWeeks(r.data.weeks || []);
    } catch {
      toast.error("Could not load saved payrolls");
    }
  }, [canManagePayrolls]);
  useEffect(() => { load(); }, [load]);

  const unsaveStub = async (stub, weekStart) => {
    if (!window.confirm(
      `Unsave ${stub.employee_name}'s pay stub for the week of ${fmtWeek(weekStart)}?\n\n` +
      "It disappears from their past pay periods and stops counting toward their vacation bank, yearly stats and CRA owed."
    )) return;
    setBusy(stub.paystub_id);
    try {
      await api.delete(`/payroll/paystub/${stub.paystub_id}`);
      toast.success(`Pay stub for ${stub.employee_name} unsaved`);
      await load();
    } catch {
      toast.error("Could not unsave that pay stub");
    } finally {
      setBusy("");
    }
  };

  const unsaveWeek = async (w) => {
    if (!window.confirm(
      `Unsave the ENTIRE payroll for ${fmtWeek(w.week_start)} (${w.stub_count} employee stub${w.stub_count === 1 ? "" : "s"})?\n\n` +
      "The week disappears from every employee's past pay periods and stops counting toward vacation banks, yearly stats and CRA owed. " +
      "Timesheets are untouched — you can re-save the payroll from the Payroll tab anytime."
    )) return;
    setBusy(w.week_start);
    try {
      await api.delete(`/payroll/week/${w.week_start}`);
      toast.success(`Payroll for ${fmtWeek(w.week_start)} unsaved`);
      await load();
    } catch {
      toast.error("Could not unsave that week");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-4" data-testid="reports-tab">
      {/* Accounting for the year — material costs from the vendor databases,
          QuickBooks invoices sent/paid/owing, overhead buckets (coming soon). */}
      {canManagePayrolls && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02]" data-testid="reports-accounting-year">
          <AccountingYearReport />
        </div>
      )}

      {/* Full payroll stats for the year — owners/admins. Gross, net, CPP,
          EI, taxes, employer costs, per-employee breakdown, T4 prep. */}
      {canManagePayrolls && (
        <div className="rounded-lg border border-white/10 bg-white/[0.02]" data-testid="reports-payroll-year">
          <PayrollYearReport />
        </div>
      )}

      <DaysMissedReport />

      {canManagePayrolls && (
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="reports-saved-payrolls">
        <div className="flex items-center justify-between">
          <h3 className="font-mono text-[10px] uppercase tracking-widest text-white/50">Saved payrolls</h3>
          <button onClick={load} data-testid="reports-saved-refresh"
            className="rounded-md border border-white/15 bg-white/5 p-1.5 text-white/70 hover:border-white/25 hover:text-white" title="Refresh">
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
        <p className="mt-1 font-mono text-[10px] text-white/35">
          Every payroll saved in the app. Unsaving removes it from past pay periods for all employees.
        </p>

        {weeks === null ? (
          <p className="mt-4 text-center font-mono text-xs text-white/40">Loading…</p>
        ) : weeks.length === 0 ? (
          <p className="mt-4 text-center font-mono text-xs text-white/40" data-testid="reports-saved-empty">
            No saved payrolls yet — save one from the Payroll tab.
          </p>
        ) : (
          <div className="mt-3 space-y-1.5">
            {weeks.map((w) => (
              <div key={w.week_start} className="rounded-md border border-white/10 bg-white/[0.02]"
                data-testid={`saved-week-${w.week_start}`}>
                <div className="flex items-center gap-2 px-3 py-2">
                  <button onClick={() => setOpen((o) => ({ ...o, [w.week_start]: !o[w.week_start] }))}
                    data-testid={`saved-week-toggle-${w.week_start}`}
                    aria-expanded={!!open[w.week_start]}
                    className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-0.5 text-left">
                    {open[w.week_start]
                      ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-white/40" />
                      : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-white/40" />}
                    <span className="font-mono text-xs font-semibold text-white">{fmtWeek(w.week_start)}</span>
                    <span className="min-w-0 basis-full font-mono text-[10px] text-white/40 sm:basis-auto">
                      {w.stub_count} stub{w.stub_count === 1 ? "" : "s"} · gross {money(w.gross)} · net {money(w.net)}
                    </span>
                  </button>
                  <button onClick={() => unsaveWeek(w)} disabled={busy === w.week_start}
                    data-testid={`saved-week-unsave-${w.week_start}`}
                    title="Unsave this entire payroll week (removes it from every employee's past pay periods)"
                    className="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-amber-800 hover:bg-amber-500/20 disabled:opacity-50 dark:text-amber-300">
                    <Undo2 className="h-3 w-3" /> {busy === w.week_start ? "…" : "Unsave week"}
                  </button>
                </div>
                {open[w.week_start] && (
                  <div className="border-t border-white/10 px-3 py-2">
                    <div className="space-y-1">
                      {w.stubs.map((s) => (
                        <div key={s.paystub_id} className="flex items-center justify-between gap-2"
                          data-testid={`saved-stub-${s.paystub_id}`}>
                          <p className="min-w-0 truncate font-mono text-[11px] text-white/60">
                            {s.employee_name} · gross {money(s.gross_pay)} · net {money(s.net_pay)}
                          </p>
                          <button onClick={() => unsaveStub(s, w.week_start)} disabled={busy === s.paystub_id}
                            data-testid={`saved-stub-unsave-${s.paystub_id}`}
                            className="shrink-0 rounded border border-white/15 bg-white/5 px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-white/60 hover:border-red-400/50 hover:text-red-400 disabled:opacity-50">
                            {busy === s.paystub_id ? "…" : "Unsave"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ReportsTab;
